import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Typography, IconButton, Box, CircularProgress, Paper, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControl,FormControlLabel,Switch, InputLabel, Select, TablePagination, InputAdornment,
  Tooltip, Snackbar, Alert, Chip, Divider, List, ListItem, ListItemText
} from '@mui/material';
import { 
  Refresh, Edit, Save, Close, Search, 
  ContentCopy, Tune, History, InfoOutlined, DeveloperBoard, PushPin, 
  AssignmentTurnedIn, CloudDownload, Sync,Business
} from '@mui/icons-material';
import { Hub } from '@mui/icons-material';
import CastConnectedIcon from "@mui/icons-material/CastConnected";
import { DeleteOutline, WarningAmber } from '@mui/icons-material';
import { AddCircleOutline } from '@mui/icons-material';
import { DeviceRegistrationDialog } from './DeviceRegistrationDialog';
import { theme } from '../../theme/themeStore';

const StatusIndicator = ({ status }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ 
      width: 8, height: 8, borderRadius: '50%', 
      bgcolor: status === 'ACTIVE' ? '#10b981' : '#94a3b8',
      boxShadow: status === 'ACTIVE' ? '0 0 8px rgba(16, 185, 129, 0.4)' : 'none'
    }} />
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: status === 'ACTIVE' ? '#10b981' : '#64748B' }}>
      {status}
    </Typography>
  </Box>
);

export const DeviceRegistry = ({ organization }) => {
  const [devices, setDevices] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [customerList, setCustomerList] = useState([]);
  const [editCid, setEditCid] = useState('');
  const [editIsDevOwned, setEditIsDevOwned] = useState(false);
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [copySuccess, setCopySuccess] = useState(false);

  // Edit States
  const [editOpen, setEditOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [editSotiName, setEditSotiName] = useState('');
  const [editTargetVersion, setEditTargetVersion] = useState('');
  const [saving, setSaving] = useState(false);

  // Logs/History States
  const [logOpen, setLogOpen] = useState(false);
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [regOpen, setRegOpen] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, msg: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteOpen] = useState(false);
  const [resetDialogOpen, setResetOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const handleRegSuccess = (msg) => {
    setFeedback({ open: true, msg: msg, severity: 'success' });
    fetchDevices();
  };

  const fetchDevices = useCallback(async () => {
    const cid = organization?.cid || organization?.id;
    if (!cid) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/devices?cid=${cid}`, {
         headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); } 
    finally { setLoading(false); }
  }, [organization]);

  const fetchVersions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const customer_type = organization?.customer_type?.toUpperCase() || 'FUEL';
      const response = await fetch(`https://console.dreamtec.io/app-versions?customer_type=${customer_type}`, {
         headers: { 
           'accept': 'application/json',
           'Authorization': `Bearer ${token}` 
         }
      });
      if (!response.ok) {
        setVersions([]);
        return;
      }
      const data = await response.json();
      setVersions(Array.isArray(data) ? data : []);
    } catch (err) { 
      console.error("Fetch versions error:", err); 
      setVersions([]); 
    }
  }, [organization?.customer_type]);

  useEffect(() => {
    fetchDevices();
    fetchVersions();
  }, [fetchDevices, fetchVersions]);

  const handleViewHistory = async (device) => {
    setSelectedDevice(device);
    setLogOpen(true);
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/devices/${device.device_id}/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDeviceLogs(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    finally { setLogsLoading(false); }
  };
const fetchSelector = async () => {
    try {
        const token = localStorage.getItem('token');
        const res = await fetch('https://console.dreamtec.io/customers/list', {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setCustomerList(data);
    } catch (err) { console.error(err); }
};

useEffect(() => {
    fetchDevices();
    fetchVersions();
    fetchSelector(); // Fetch the company names once
}, [fetchDevices, fetchVersions]);
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
  };

  const handleEditClick = (device) => {
    setSelectedDevice(device);
    setEditSotiName(device.device_label || '');
    setEditIsDevOwned(device.is_dev_owned || false);
    setEditCid(device.cid);
    setEditTargetVersion(device.target_version || 'None');
    setEditOpen(true);
  };

  // const handleSaveEdit = async () => {
  //   setSaving(true);
  //   try {
  //     const token = localStorage.getItem('token');
  //     const versionToSave = editTargetVersion === 'None' ? null : editTargetVersion;
  //     const response = await fetch(`https://console.dreamtec.io/devices/${selectedDevice.device_id}`, {
  //       method: 'PATCH',
  //       headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ device_label: editSotiName, target_version: versionToSave })
  //     });
  //     if (response.ok) { setEditOpen(false); fetchDevices(); }
  //   } finally { setSaving(false); }
  // };

  const handleSotiNavigate = (imei) => {
    const url = new URL(`https://soti.dreamtecsystems.com/MobiControl/WebConsole/home/devices/${imei}/DeviceDetails`);
    url.searchParams.set("search", `DeviceId Equal "${imei}"`);
    url.searchParams.set("subGroups", "false");
    window.open(url, "_blank");
  }

  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setDeleteOpen(true);
  };

  const handleSoftReset = (device) => {
    setDeviceToDelete(device);
    setResetOpen(true);
  };

  const confirmSoftReset = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/devices/${deviceToDelete.device_id}/soft-reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setFeedback({ open: true, msg: 'Device reset successfully', severity: 'success' });
       
      } else { throw new Error("Failed to reset device"); }
    } catch (err) {
      setFeedback({ open: true, msg: err.message, severity: 'error' });
    } finally {
      setSaving(false);
      setResetOpen(false);
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://console.dreamtec.io/devices/${deviceToDelete.device_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setFeedback({ open: true, msg: 'Device removed successfully', severity: 'success' });
        fetchDevices();
      } else { throw new Error("Failed to delete device"); }
    } catch (err) {
      setFeedback({ open: true, msg: err.message, severity: 'error' });
    } finally {
      setSaving(false);
      setDeleteOpen(false);
    }
  };

  const handleSaveEdit = async () => {
  setSaving(true);
  console.log('..........',editCid)
  try {
    const token = localStorage.getItem('token');
    const versionToSave = editTargetVersion === 'None' ? null : editTargetVersion;

    const response = await fetch(`https://console.dreamtec.io/devices/${selectedDevice.device_id}`, {
      method: 'PATCH',
      headers: { 
        'Authorization': `Bearer ${token}`, 
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify({ 
        device_label: editSotiName, 
        target_version: versionToSave,
        cid: parseInt(editCid),                // New CID from Dropdown
        is_dev_owned: editIsDevOwned   // The Dev-Owned flag
      })
    });

    if (response.ok) { 
      setEditOpen(false); 
      fetchDevices(); // Refresh the list
    }
  } finally {
    setSaving(false);
  }
};

  // Pagination Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredDevices = devices.filter(d => 
    d.device_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (d.imei && d.imei.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (d.device_label && d.device_label.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleSyncSoti = async () => {
  


    setSyncing(true);
    try {
        const token = localStorage.getItem('token');
        const cid = organization?.cid || organization?.id;
        console.log('Syncing SOTI for CID:', cid, 'Organization:', organization);
        const sotiGroupPath = organization?.soti_group_path || '';
        if (!cid || !sotiGroupPath) {
            throw new Error("Missing CID or SOTI Group Path");
        }

        // Use URLSearchParams to handle the backslashes correctly in the URL
        const params = new URLSearchParams({ group_path: sotiGroupPath });

        const response = await fetch(`https://console.dreamtec.io/devices/sync/${cid}?${params.toString()}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });

        const data = await response.json();

        if (response.ok) {
            setFeedback({ 
                open: true, 
                msg: `Successfully synced ${data.synced_count} devices from SOTI`, 
                severity: 'success' 
            });
            fetchDevices(); // Refresh the table
        } else {
            throw new Error(data.detail || 'Sync failed');
        }
    } catch (err) {
        setFeedback({ open: true, msg: err.message, severity: 'error' });
    } finally {
        setSyncing(false);
    }
};




  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#2F727C' }} /></Box>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mt: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
          Registry: {filteredDevices.length} Devices
        </Typography>

<Stack direction="row" spacing={1.5} alignItems="center">
  <TextField
    size="small"
    placeholder="Search IMEI or ID..."
    value={searchQuery}
    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
    InputLabelProps={{style: {fontSize: 40}}}
    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
    sx={{ width: 220, '& .MuiOutlinedInput-root': {  bgcolor: '#fff' } }}
  />

  {/* --- NEW SYNC BUTTON --- */}
  <Button 
    variant="outlined" 
    startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Sync />}
    onClick={handleSyncSoti}
    disabled={syncing}
    color="primary"
  >
    {syncing ? 'Syncing...' : 'Sync SOTI'}
  </Button>

  <Button 
    variant="contained" 
    startIcon={<AddCircleOutline />}
    onClick={() => setRegOpen(true)}
    color="primary"
  >
    Register New Device
  </Button>
</Stack>
      </Stack>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#F8FAFC' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: '#64748B', py: 1.5, fontSize: '0.7rem' }}>IMEI & DEVICE ID</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#64748B', fontSize: '0.7rem' }}>SOTI NAME</TableCell>
                  {(organization.cid === 1000) && (
      <TableCell sx={{ fontWeight: 800, fontSize: '0.7rem' }}>CURRENT ASSIGNMENT</TableCell>
    )}
              <TableCell sx={{ fontWeight: 800, color: '#64748B', fontSize: '0.7rem' }}>DEVICE INFORMATION</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#64748B', fontSize: '0.7rem' }}>STATUS</TableCell>
              <TableCell sx={{ fontWeight: 800, color: '#64748B', fontSize: '0.7rem' }}>APP VERSION</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, color: '#64748B', fontSize: '0.7rem' }}>ADMIN</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDevices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((device) => (
              <TableRow key={device.device_id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 800, fontFamily: 'monospace', fontSize: '0.85rem' }}>{device.device_id}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>{device.imei}</Typography>
                    <Tooltip title="Copy IMEI"><IconButton size="small" onClick={() => handleCopy(device.imei)} sx={{ p: 0.2 }}><ContentCopy sx={{ fontSize: 12, opacity: 0.5 }} /></IconButton></Tooltip>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#2F727C' }}>{device.device_label || '—'}</Typography>
                    <Tooltip title="View in SOTI" arrow>
                      <IconButton 
                        size="small" 
                        onClick={() => handleSotiNavigate(device.device_id)} 
                        sx={{ p: 0.5, bgcolor: 'rgba(16, 185, 129, 0.05)', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.15)' } }}
                      >
                        <CastConnectedIcon sx={{ fontSize: 16, color: '#10b981' }} /> 
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                     {organization.cid === 1000 && (
        <TableCell>
          <Box sx={{ 
            display: 'inline-flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, 
            borderRadius: '6px', bgcolor: device.cid === 1000 ? '#F1F5F9' : '#FFF4E5',
            border: device.cid === 1000 ? '1px solid #E2E8F0' : '1px solid #FFD591'
          }}>
            <Business sx={{ fontSize: 14, color: device.cid === 1000 ? '#64748B' : '#D97706' }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.7rem', color: device.cid === 1000 ? '#64748B' : '#D97706' }}>
              {device.cid === 1000 ? "Demo (1000)" : `${device.customer_name} (${device.cid})`}
            </Typography>
          </Box>
        </TableCell>
      )}

                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569', mb: 0.5 }}>{device.manufacturer} {device.model}</Typography>
                  <Chip label={`Android v${device.os_version}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 900, color: '#64748B', bgcolor: '#F8FAFC', borderRadius: '4px' }} />
                </TableCell>

                <TableCell>
                  <StatusIndicator status={device.status} />
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                    {device.last_seen_at ? new Date(device.last_seen_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'Never Active'}
                  </Typography>
                </TableCell>

<TableCell>
  <Stack spacing={0.8} alignItems="flex-start">
    
    {/* 1. INSTALLED VERSION CHIP */}
    <Box sx={{ 
      display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 1, py: 0.3, 
      bgcolor: '#F1F5F9', borderRadius: '6px', border: '1px solid #E2E8F0' 
    }}>
      <History sx={{ fontSize: 13, color: '#64748B' }} />
      <Typography sx={{ 
        fontSize: '0.7rem', fontWeight: 800, color: '#475569', 
        fontFamily: 'JetBrains Mono, monospace' 
      }}>
        v{device.last_reported_version || '0.0.0'}
      </Typography>
    </Box>

    {/* 2. PINNED VERSION CHIP (Conditional) */}
    {device.target_version && (
      <Box sx={{ 
        display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 1, py: 0.3, 
        bgcolor: '#FFFBEB', borderRadius: '6px', border: '1px solid #FEF3C7',
        boxShadow: '0 2px 4px rgba(180, 83, 9, 0.05)'
      }}>
        <PushPin sx={{ fontSize: 11, color: '#B45309' }} />
        <Typography sx={{ 
          fontSize: '0.65rem', fontWeight: 900, color: '#B45309', 
          letterSpacing: '0.02rem'
        }}>
          PINNED: <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace', ml: 0.5 }}>
            v{device.target_version}
          </Box>
        </Typography>
      </Box>
    )}
  </Stack>
</TableCell>

                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Activity Logs"><IconButton size="small" onClick={() => handleViewHistory(device)} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}><History fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Configure Policy"><IconButton size="small" onClick={() => handleEditClick(device)} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}><Tune fontSize="small" sx={{ color: '#2F727C' }} /></IconButton></Tooltip>
                    <Tooltip title="Soft Reset"><IconButton size="small" onClick={() => handleSoftReset(device)} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}><Sync fontSize="small" sx={{ color: '#2F727C' }} /></IconButton></Tooltip>
                    <Tooltip title="Remove Device"><IconButton size="small" onClick={() => handleDeleteClick(device)} sx={{ border: '1px solid #fee2e2', borderRadius: '8px', bgcolor: '#fff5f5' }}><DeleteOutline fontSize="small" sx={{ color: '#d32f2f' }} /></IconButton></Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredDevices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}
        />
      </TableContainer>

      <DeviceRegistrationDialog 
        open={regOpen} 
        onClose={() => setRegOpen(false)} 
        organization={organization}
        onSuccess={handleRegSuccess}
      />

      {/* History Dialog */}
      <Dialog open={logOpen} onClose={() => setLogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 900, borderBottom: '1px solid #E2E8F0' }}>
            Check-in History: {selectedDevice?.device_label || selectedDevice?.device_id}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
            {logsLoading ? (
                <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={30} /></Box>
            ) : (
                <List sx={{ py: 0 }}>
                    {deviceLogs.length > 0 ? deviceLogs.map((log, i) => (
                        <React.Fragment key={i}>
                            <ListItem alignItems="flex-start">
                                <Box sx={{ mr: 2, mt: 0.5 }}>
                                    {log.action_taken === 'APK_SENT' ? <CloudDownload color="primary" /> : <AssignmentTurnedIn color="success" />}
                                </Box>
                                <ListItemText 
                                    primary={<Typography variant="body2" sx={{ fontWeight: 800 }}>{log.action_taken.replace('_', ' ')}</Typography>}
                          secondary={
  <>
    <Typography
      variant="caption"
      color="textSecondary"
      sx={{ display: 'block' }}
    >
      {new Date(log.checked_in_at).toLocaleString()}
    </Typography>

    {log.action_taken !== 'REGISTER' && (
      <Typography
        variant="caption"
        sx={{ fontWeight: 600 }}
      >
        {log.reported_version && `Reported: v${log.reported_version}`}
        {log.reported_version && log.served_version && ' | '}
        {log.served_version && `Served: v${log.served_version}`}
      </Typography>
    )}
  </>
}
                                />
                            </ListItem>
                            {i < deviceLogs.length - 1 && <Divider />}
                        </React.Fragment>
                    )) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="body2" color="textSecondary">No activity records found.</Typography></Box>
                    )}
                </List>
            )}
        </DialogContent>
        <DialogActions><Button onClick={() => setLogOpen(false)} sx={{ fontWeight: 800 }}>Close</Button></DialogActions>
      </Dialog>

<Dialog 
  open={editOpen} 
  onClose={() => setEditOpen(false)} 
  fullWidth 
  maxWidth="xs" 
  PaperProps={{ sx: { borderRadius: '16px' } }}
>
  <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <DeveloperBoard sx={{ color: '#2F727C' }} /> Device Policy
  </DialogTitle>
  
  <DialogContent dividers>
    <Stack spacing={3} sx={{ mt: 1 }}>
      
      {/* 1. SYSTEM IDENTIFIERS (READ ONLY) */}
      <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#94a3b8', display: 'block', mb: 1 }}>
            SYSTEM IDENTIFIERS
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            <b>ID:</b> {selectedDevice?.device_id}
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            <b>IMEI:</b> {selectedDevice?.imei}
          </Typography>
      </Box>
{/* DEVELOPER FLAG TOGGLE */}
{organization.cid === 1000 && (
<Box sx={{ 
  p: 1.5, 
  bgcolor: editIsDevOwned ? 'rgba(246, 185, 59, 0.1)' : '#F8FAFC', 
  borderRadius: '12px', 
  border: editIsDevOwned ? '1px solid #F6B93B' : '1px solid #E2E8F0',
  mb: 2 
}}>
  <FormControlLabel
    control={
      <Switch 
        checked={editIsDevOwned} 
        onChange={(e) => setEditIsDevOwned(e.target.checked)} 
        color="warning" 
      />
    }
    label={
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 800, color: editIsDevOwned ? '#B7791F' : '#64748B' }}>
          DEVELOPER OWNED
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
          Visible in Dev Hub regardless of assigned CID
        </Typography>
      </Box>
    }
  />
</Box>
)}
      {/* 2. SOTI NAME (ALWAYS SHOWN) */}
      <TextField 
        label="SOTI Name" 
        fullWidth 
        value={editSotiName} 
        onChange={(e) => setEditSotiName(e.target.value)} 
        variant="outlined" 
      />

      {/* 3. TARGET CUSTOMER (CONDITIONAL: ONLY IF CID IS 1000) */}
      {(organization?.cid === 1000 || organization?.id === 1000) && (
        <FormControl fullWidth variant="outlined">
          <InputLabel>Target Company Assignment</InputLabel>
          <Select
            label="Target Company Assignment"
            value={editCid}
            onChange={(e) => setEditCid(e.target.value)}
            sx={{ borderRadius: '10px' }}
          >
            {customerList.map((cust) => (
              <MenuItem key={cust.cid} value={cust.cid}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cust.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', ml: 2 }}>ID: {cust.cid}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" sx={{ mt: 0.5, ml: 1, color: '#2F727C', fontWeight: 600 }}>
            Device will route to this company's environment.
          </Typography>
        </FormControl>
      )}

      {/* 4. VERSION PINNING (ALWAYS SHOWN) */}
      <FormControl fullWidth variant="outlined">
        <InputLabel>Target App Version (Pinned)</InputLabel>
        <Select 
          value={editTargetVersion} 
          onChange={(e) => setEditTargetVersion(e.target.value)} 
          label="Target App Version (Pinned)"
          sx={{ borderRadius: '10px' }}
        >
          <MenuItem value="None"><em>Follow Global Policy (Auto)</em></MenuItem>
          {versions.map((v) => (
            <MenuItem key={v.version_label} value={v.version_label}>
                v{v.version_label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

    </Stack>
  </DialogContent>

  <DialogActions sx={{ p: 3 }}>
    <Button onClick={() => setEditOpen(false)} color="error" >Cancel</Button>
    <Button 
      onClick={handleSaveEdit} 
      variant="contained" 
      disabled={saving} 
      sx={{ bgcolor: '#2F727C', fontWeight: 900, borderRadius: '8px', px: 3 }}
    >
      {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
    </Button>
  </DialogActions>
</Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteOpen(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmber sx={{ color: theme.palette.error.main }} /> Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete device <b>{deviceToDelete?.device_label || deviceToDelete?.device_id}</b>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#F8FAFC' }}>
          <Button onClick={() => setDeleteOpen(false)} color="error">Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" disabled={saving} color="error" >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

            <Dialog open={resetDialogOpen} onClose={() => setResetOpen(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmber sx={{ color: theme.palette.error.main }} /> Confirm Soft Reset
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to soft reset device <b>{deviceToDelete?.device_label || deviceToDelete?.device_id}</b>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#F8FAFC' }}>
          <Button onClick={() => setResetOpen(false)} color="error">Cancel</Button>
          <Button onClick={confirmSoftReset} variant="contained" disabled={saving} color="primary" >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Soft Reset Device'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={copySuccess} autoHideDuration={2000} onClose={() => setCopySuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" sx={{ fontWeight: 700 }}>IMEI Copied to Clipboard</Alert>
      </Snackbar>
    </Box>
  );
};